import React from "react"
import H1 from "../../atoms/typography/H1"
import H2 from "../../atoms/typography/H2"
import H3 from "../../atoms/typography/H3"
import { theme } from "../../theme"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import styled from "styled-components"
import BodyLarge from "../../atoms/typography/BodyLarge"
import Button from "../../atoms/buttons/Button"
import Body from "../../atoms/typography/Body"
import ContactUsForm from "../../organisms/ContactUsForm"
import { ArrowButtonDown } from "../../atoms/icons/ArrowButtonIcons"
import { useScrollToRef } from "../../../utils/helpers"

const ColoredTitle = styled(H1)`
  background: ${(props) => props.theme.gradients.blue};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const WaveSectionSvgStyle = styled.svg`
  position: absolute;
  top: -400px;
  right: 0;
  width: 100%;
  min-width: 1432px; // width of the svg box
`

const WaveSection = () => (
  <WaveSectionSvgStyle xmlns="http://www.w3.org/2000/svg" width="1280" fill="none" viewBox="0 0 1280 509">
    <path
      fill="url(#paint0_linear_8394_15317)"
      fillOpacity="1.0"
      d="M-30 0h1414l18 474.5S1213 519 1041.5 506c-160.287-12.15-262.945-90.1-423-105-139.974-13.031-221.235-6.905-359.5 18.5C143.131 440.79-30 506-30 506z"
    ></path>
    <defs>
      <linearGradient
        id="paint0_linear_8394_15317"
        x1="50.978"
        x2="1151.32"
        y1="92"
        y2="473.845"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#81A6C8"></stop>
        <stop offset="1" stopColor="#97D1D9"></stop>
      </linearGradient>
    </defs>
  </WaveSectionSvgStyle>
)

const NAVBAR_OFFSET = 160

const NetworkProductPage: React.FC = () => {
  const scrollToRef = useScrollToRef(NAVBAR_OFFSET)
  const contactRef = React.useRef<HTMLDivElement>(null)
  const readMoreRef = React.useRef<HTMLDivElement>(null)

  const handleScrollToContact = () => {
    scrollToRef(contactRef)
  }

  const handleScrollToReadMore = () => {
    scrollToRef(readMoreRef)
  }

  return (
    <div>
      {/* hero section */}
      <div id="hero-section" style={{ position: "relative", backgroundColor: "#1A1D28" }}>
        <Visible xs>
          <Container>
            <HorizontalSpacer spacing={2} />
            <Row justify="center">
              <Col lg={8}>
                <H1 bold color={theme.colors.white}>
                  Satcube networks
                </H1>
                <H3 color={theme.colors.white}>Bespoke satellite networks for secure communications.</H3>
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />
          </Container>
        </Visible>
        <Hidden xs>
          <Container fluid style={{ position: "absolute", width: "100%", height: "100%" }}>
            <Row nogutter direction="column" align="center" justify="between" style={{ width: "100%", height: "100%" }}>
              <Col style={{ padding: "80px" }}>
                <Row>
                  <H1 bold color={theme.colors.white}>
                    Satcube networks
                  </H1>
                </Row>
                <Row>
                  <H3 color={theme.colors.white}>Bespoke satellite networks for secure communications.</H3>
                </Row>
              </Col>
              <Hidden xs sm>
                <Row justify="center" align="center">
                  <Col xs="content" style={{ cursor: "pointer" }} tabIndex={0} onClick={handleScrollToReadMore}>
                    <Row justify="center" align="center">
                      <ArrowButtonDown color="white" />
                      <Spacer spacing={1.5} />
                      <H2 color="white">Read more</H2>
                    </Row>
                    <HorizontalSpacer spacing={2} />
                  </Col>
                </Row>
              </Hidden>
            </Row>
          </Container>
        </Hidden>
        <img
          src="https://images.ctfassets.net/kzqg847wz5jr/6FGcnkna1QXpajiGo6VxZf/234f092efa89324934d3f82294b496b2/Networks-header.webp"
          style={{ maxWidth: "100vw " }}
        />
      </div>
      {/* text section */}
      <div id="text-section" style={{ backgroundColor: "#1A1D28" }} ref={readMoreRef}>
        <Container>
          <Row justify="center">
            <Col lg={8}>
              <HorizontalSpacer spacing={2} />
              <ColoredTitle color={theme.gradients.blue}>
                Secure communications with resilient satellite networks
              </ColoredTitle>
              <HorizontalSpacer spacing={2} />
              <BodyLarge color={theme.colors.white}>
                In today's interconnected world, reliable communication is not just an asset, it's a lifeline —
                especially in critical situations. At Satcube, we understand this need deeply, which is why we offer
                comprehensive coverage solutions tailored to your exact requirements. We provide Bespoke Networks,
                designed to ensure optimal performance with our compact and portable Ku-band antennas. This approach
                maximizes link availability, keeping you connected when it counts. Beside bespoke networks, Satcube is a
                provider of Intelsat FlexMove, a managed global satellite service providing coverage across the globe,
                optimal for use with Satcube's portable and mobile products.
              </BodyLarge>
              <HorizontalSpacer spacing={2} />
              <Row justify="center">
                <Button title="Contact us" variant="blue" onPress={handleScrollToContact} />
              </Row>
              <HorizontalSpacer spacing={4} />
            </Col>
          </Row>
        </Container>
      </div>
      {/* grey background gradient */}
      <div style={{ background: "linear-gradient(90deg, #585858, #444444)" }}>
        {/* image (left) and text (right) section */}
        <div id="image-text-section">
          {/* image left */}
          <Row nogutter>
            <Col md={4}>
              <img
                style={{ width: "100%", objectFit: "cover" }}
                src="https://images.ctfassets.net/kzqg847wz5jr/VkGxxe273MpQAT054DTMk/9ab9b13a19377d08cb5d21d9fead1a57/Networks_-_Bespoke.webp"
              />
            </Col>
            {/* text right */}
            <Col md={8}>
              <Container style={{ height: "100%" }}>
                <Row justify="center" align="center" style={{ height: "100%" }}>
                  <Col lg={10}>
                    <HorizontalSpacer spacing={2} />
                    <H1 bold color="white">
                      Satcube Bespoke Networks
                    </H1>
                    <HorizontalSpacer spacing={2} />
                    <BodyLarge color="white">
                      In an ever evolving interference landscape, agility is key. Our bespoke software-defined services
                      and satellite-agnostic networks adapt swiftly to changing conditions, ensuring uninterrupted
                      connectivity.
                    </BodyLarge>
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </div>
        {/* centered column */}
        <HorizontalSpacer spacing={4} />
        <Container>
          <Row justify="center">
            <Col lg={8}>
              {/* about section */}
              <div id="about-section">
                <H2 color="white">About the bespoke network</H2>
                <HorizontalSpacer spacing={2} />
                <ul style={{ color: "white" }}>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Strategic Satellite Placement:</strong> Satellite coverage tailored to your needs with the
                      ability to expand coverage as the need arises.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Traffic priority and control:</strong> Tailor traffic priority to suit your applications,
                      maintaining a set speed even in case of interference.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>High speed service:</strong> At up to 85 Mb/s down and 20 Mb/s up, network speeds can be
                      adjusted to suit your use-case.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Scalable Solutions:</strong> Our network supports from tens to thousands of portable and
                      mobility terminals, growing with your needs.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Customer-Centric Management:</strong> Network management is customized to your specific
                      requirements, putting you in control.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Network Operations Center:</strong> The Satcube NOC is available for support and network
                      management at all times.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.white}>
                      <strong>Satcube Ecosystem:</strong> The Satcube products and network services are co-developed to
                      provide optimal performance and user experience.
                    </BodyLarge>
                  </li>
                </ul>
              </div>

              <HorizontalSpacer spacing={2} />

              {/* specs section */}
              <div id="specs-section">
                <Row>
                  <Col lg={6}>
                    {/* Security Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Security</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>AES256 encryption</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>IPSEC crypto supported</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Latest cybersecurity practices</BodyLarge>
                        </li>
                      </ul>
                    </div>
                    <HorizontalSpacer spacing={2} />
                    {/* Access Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Access</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>
                            X509 authentication for all network components
                          </BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Satcube authenticity validation layer</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Restricted teleport access</BodyLarge>
                        </li>
                      </ul>
                    </div>
                    <HorizontalSpacer spacing={2} />
                    {/* Service Level Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Service level</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>Peacetime uptime 99.5%</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Interference uptime &gt;95%</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Definable QoS</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>CIR / MIR</BodyLarge>
                        </li>
                      </ul>
                    </div>
                    {/* Software-defined hub architecture */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Software-defined hub architecture</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>Fast recovery in case of failures</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Fast configuration of new networks</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Unparalleled flexibility</BodyLarge>
                        </li>
                      </ul>
                    </div>
                  </Col>
                  <Col lg={6}>
                    {/* Data Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Data</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>Raw internet access</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>
                            Ground station tunnel to HQ or command center available
                          </BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Adaptable traffic priority</BodyLarge>
                        </li>
                      </ul>
                    </div>
                    <HorizontalSpacer spacing={2} />
                    {/* Redundancy Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Redundancy</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>
                            Contingency plans for all critical components
                          </BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Hot redundancy where possible</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>Teleport and satellite redundancies.</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>GPS jamming resilient system</BodyLarge>
                        </li>
                      </ul>
                    </div>
                    <HorizontalSpacer spacing={2} />
                    {/* Redundancy Section */}
                    <div style={{ color: "white" }}>
                      <H3 color={theme.colors.white}>
                        <strong>Interference Detection and Automated Response (IDAR)</strong>
                      </H3>
                      <HorizontalSpacer spacing={1} />
                      <ul>
                        <li>
                          <BodyLarge color={theme.colors.white}>Interference detection and categorization</BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>
                            Monitoring and logging for pattern recognition and capability mapping
                          </BodyLarge>
                        </li>
                        <li>
                          <BodyLarge color={theme.colors.white}>
                            Situation-tailored automatic response ensures maximum uptime and service throughput
                          </BodyLarge>
                        </li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>

        <HorizontalSpacer spacing={2} />

        {/* image section */}
        <div id="image-section">
          <Hidden xs>
            <img
              src="https://images.ctfassets.net/kzqg847wz5jr/7hPGczE7GzpHWsdLECB3bR/7b9dfe304398893ae158b555aefba76b/network_infographic.png"
              style={{ width: "100%", maxWidth: "100vw" }}
            />
          </Hidden>
          <Visible xs>
            <img
              src="https://images.ctfassets.net/kzqg847wz5jr/543POz8fcZwsughNdIFnaX/3ed60e1a5ca1467a210fdd24358f2438/network_infographic_mobile.png"
              style={{ width: "100%", maxWidth: "100vw" }}
            />
          </Visible>
          <Container>
            <Row justify="center">
              <Col lg={8}>
                <HorizontalSpacer spacing={4} />
                <H2 color={theme.colors.white} textAlign="center">
                  Reach out to learn more about how a Bespoke Network can secure your communication needs
                </H2>
                <HorizontalSpacer spacing={2} />
                <Row justify="center">
                  <Button title="Contact us" variant="blue" onPress={handleScrollToContact} />
                </Row>
                <HorizontalSpacer spacing={6} />
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      {/* flexmove section */}
      <div id="flexmove-section" style={{ position: "relative", overflow: "hidden" }}>
        {/* wave */}
        <div style={{ position: "relative" }}>
          <WaveSection />
        </div>
        {/* content */}
        <div>
          <Visible xs>
            <HorizontalSpacer spacing={22} />
            <img
              src="https://images.ctfassets.net/kzqg847wz5jr/2wTqNMSnveEmOdQBKXtZwv/0e9c766feb59ceaada88f9ac3f5db396/Networks_-_Flexmove.webp"
              style={{ width: "100%" }}
            />
            <HorizontalSpacer spacing={2} />
          </Visible>
          <Container>
            <Row justify="center">
              <Col lg={8}>
                <Hidden xs>
                  <HorizontalSpacer spacing={16} />
                  <img
                    src="https://images.ctfassets.net/kzqg847wz5jr/2wTqNMSnveEmOdQBKXtZwv/0e9c766feb59ceaada88f9ac3f5db396/Networks_-_Flexmove.webp"
                    style={{ width: "100%" }}
                  />
                </Hidden>
                <HorizontalSpacer spacing={2} />
                <H1 bold color={theme.colors.dark}>
                  Intelsat FlexMove
                </H1>
                <HorizontalSpacer spacing={1} />
                <BodyLarge color={theme.colors.dark}>
                  FlexMove provides high throughput satellite (HTS) broadband internet connectivity globally. Satcube
                  products paired with Intelsat Flexmove offers users the ability to access high throughput satellite
                  internet globally, in under a minute to perform mission critical applications.
                </BodyLarge>
                <HorizontalSpacer spacing={1} />
                <ul>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>High-speed service:</strong> The information rate for the service is set at 10 Mbps
                      download and 3 Mbps upload, ensuring consistent and reliable data transfer-speed for your
                      necessary applications.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>Global coverage:</strong> The FlexMove Service offers broad coverage through a network of
                      over 50 HTS and widebeam GEO-satellites, which ensures you to stay connected reliably across the
                      globe.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>iDirect IQ 200 or iDirect 950 mp:</strong> Select your preferred modem. With just one
                      configuration you can access the entire network seamlessly using the Velocity platform.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>Flexible data packages:</strong> Choose from a variety of data packages available for
                      monthly, quarterly and annual renewal, designed to fit all types of users. Additionally, top-up
                      packages are available for purchase whenever you need extra data.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>Top up packages:</strong> Additional packages are available for purchase whenever you need
                      extra data to ensure you can stay connected without any interruption.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>Non-stopping Service:</strong> To ensure you can stay connected without any interruption,
                      the service will continue to operate at full speed even if the data limit is exceeded. Instead of
                      reducing speed or stopping, an overage charge will be applied to cover the additional usage.
                    </BodyLarge>
                  </li>
                  <li>
                    <BodyLarge color={theme.colors.dark}>
                      <strong>24/7 Support:</strong> Our customer support team is available around the clock to assist
                      you with any issues or questions you may have.
                    </BodyLarge>
                  </li>
                </ul>
                <HorizontalSpacer spacing={6} />
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div ref={contactRef}>
        <ContactUsForm />
      </div>
    </div>
  )
}

export default NetworkProductPage
